import { useQueries, useQuery } from '@tanstack/react-query';

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Add, Apps, ArrowForward, ContentCopy, Delete, Download, Edit, History, MoreVert, Refresh, Search } from '@mui/icons-material';
import ArticleIcon from '@mui/icons-material/Article';
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	InputAdornment,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';

import { UserInterfaceClient } from '@@api';
import { formatTimestampRelative, useApiClient, usePersistedTablePagination, useToastService } from '@@common';
import { ConfirmDialog, DialogHeaderActions, EntityCard, EntityCardDetails, EntityCardPlaceholder, GalleryHeader, GalleryStatistics } from '@@components';

import { BundleDefinitionSummaryModel, ChangeSetClient } from '../../api/api';
import { useChangeSet } from '../../components/ChangeSetContext';
import { useDesignerScreenContext } from '../../components/Chat/DesignerScreenContext';
import { PresenceIndicator } from '../../components/Presence/PresenceIndicator';
import { usePresence } from '../../components/Presence/usePresence';
import AppVersionsDialog from './Components/AppVersionsDialog';
import CreateAppDialog from './Components/CreateAppDialog';
import { Bundle } from './Models/Bundle';
import { downloadAppReactCode } from './Utils/AppReactCodeDownload';

type AppCardProps = {
	bundle: BundleDefinitionSummaryModel;
	details: AppDetails;
	onShowVersions: (bundle: BundleDefinitionSummaryModel) => void;
	onCopyId: (bundle: BundleDefinitionSummaryModel) => void;
	onDownloadReactCode: (bundle: BundleDefinitionSummaryModel) => void;
	onEdit: (bundle: BundleDefinitionSummaryModel) => void;
	onDelete: (bundle: BundleDefinitionSummaryModel) => void;
};

type AppDetails = {
	description?: string;
	definition?: string;
};

const emptyAppDetails: AppDetails = {};

function getAppDetails(definition?: string): AppDetails {
	if (!definition) return emptyAppDetails;

	try {
		const bundle = JSON.parse(definition) as Bundle;
		return {
			description: bundle.Description,
			definition,
		};
	} catch {
		return emptyAppDetails;
	}
}

function AppCard({ bundle, details, onShowVersions, onCopyId, onDownloadReactCode, onEdit, onDelete }: AppCardProps) {
	const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
	const { currentChangeSetId } = useChangeSet();
	const changeSetId = currentChangeSetId?.trim() || bundle.changeSetId?.trim() || '';
	const viewers = usePresence('app', bundle.definitionId, `changeset:${changeSetId}`, null, true);

	return (
		<EntityCard
			sx={{
				p: 2.25,
				pb: 1,
				height: 320,
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Stack sx={{ flexGrow: 1, minHeight: 0 }}>
				<Stack direction="row" spacing={1.5} alignItems="flex-start">
					<Box
						sx={{
							width: 46,
							height: 46,
							borderRadius: 2,
							display: 'grid',
							placeItems: 'center',
							flexShrink: 0,
							backgroundColor: 'primary.main',
							color: 'white',
						}}
					>
						<Apps />
					</Box>
					<Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
						<Typography component="h4" variant="subtitle1" fontWeight={500} noWrap title={bundle.name} sx={{ minWidth: 0 }}>
							{bundle.name}
						</Typography>
						<PresenceIndicator viewers={viewers} />
					</Stack>
					<Stack direction="row" spacing={0.25}>
						<Tooltip title="Versions">
							<IconButton aria-label={`View versions for ${bundle.name}`} size="small" onClick={() => onShowVersions(bundle)}>
								<History fontSize="small" />
							</IconButton>
						</Tooltip>
						<IconButton aria-label={`More actions for ${bundle.name}`} size="small" onClick={(event) => setActionsAnchor(event.currentTarget)}>
							<MoreVert fontSize="small" />
						</IconButton>
					</Stack>
				</Stack>
				<Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mt: 1, minWidth: 0, flexGrow: 1 }}>
					<Typography
						variant="body2"
						color="text.secondary"
						title={details.description}
						sx={{ minWidth: 0, flexGrow: 1, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}
					>
						{details.description || 'No description'}
					</Typography>
					<Tooltip title="Edit app description">
						<IconButton size="small" aria-label={`Edit description for ${bundle.name}`} onClick={() => onEdit(bundle)} sx={{ flexShrink: 0, p: 0.25 }}>
							<Edit sx={{ fontSize: 14 }} />
						</IconButton>
					</Tooltip>
				</Stack>
				{bundle.tags && bundle.tags.length > 0 && (
					<Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
						{bundle.tags.slice(0, 3).map((tag) => (
							<Chip key={tag} label={tag} size="small" variant="outlined" />
						))}
						{bundle.tags.length > 3 && <Chip label={`+${bundle.tags.length - 3}`} size="small" variant="outlined" />}
					</Stack>
				)}
			</Stack>
			<EntityCardDetails
				horizontalPadding={2.25}
				metadata={
					<Stack direction="row" spacing={1} alignItems="center">
						<Chip label={`v${bundle.versionId ?? '—'}`} size="small" />
						<Typography variant="body2" color="text.secondary" noWrap>
							{bundle.updatedDate ? `Updated ${formatTimestampRelative(bundle.updatedDate)}` : 'Not updated yet'}
						</Typography>
					</Stack>
				}
				metadataSx={{ mt: 1 }}
				statistics={[
					{ label: 'Views', value: bundle.statistics?.viewCount ?? 0 },
					{ label: 'Components', value: bundle.statistics?.componentCount ?? 0 },
					{ label: 'Media', value: bundle.statistics?.resourceCount ?? 0 },
					{ label: 'Operations', value: bundle.statistics?.operationCount ?? 0 },
					{ label: 'Contexts', value: bundle.statistics?.contextCount ?? 0 },
				]}
				action={
					<Button
						component={Link}
						to={`/app/${bundle.definitionId}`}
						endIcon={<ArrowForward />}
						disabled={!bundle.definitionId}
						sx={{ alignSelf: 'stretch', justifyContent: 'flex-start', width: '100%', color: 'text.primary' }}
					>
						Open app
					</Button>
				}
			/>
			<Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={() => setActionsAnchor(null)}>
				<MenuItem component={Link} to={`/app/${bundle.definitionId}/resources`} disabled={!bundle.definitionId}>
					<ListItemIcon>
						<ArticleIcon />
					</ListItemIcon>
					<ListItemText>Resources</ListItemText>
				</MenuItem>
				<MenuItem
					onClick={() => {
						onEdit(bundle);
						setActionsAnchor(null);
					}}
				>
					<ListItemIcon>
						<Edit fontSize="small" />
					</ListItemIcon>
					<ListItemText>Edit app</ListItemText>
				</MenuItem>
				<MenuItem
					onClick={() => {
						onCopyId(bundle);
						setActionsAnchor(null);
					}}
				>
					<ListItemIcon>
						<ContentCopy fontSize="small" />
					</ListItemIcon>
					<ListItemText>Copy ID</ListItemText>
				</MenuItem>
				<MenuItem
					disabled={!bundle.definitionId}
					onClick={() => {
						onDownloadReactCode(bundle);
						setActionsAnchor(null);
					}}
				>
					<ListItemIcon>
						<Download fontSize="small" />
					</ListItemIcon>
					<ListItemText>Download React code (.zip)</ListItemText>
				</MenuItem>
				<MenuItem
					onClick={() => {
						onDelete(bundle);
						setActionsAnchor(null);
					}}
					sx={{ color: 'error.main' }}
				>
					<ListItemIcon sx={{ color: 'inherit' }}>
						<Delete fontSize="small" />
					</ListItemIcon>
					<ListItemText>Delete app</ListItemText>
				</MenuItem>
			</Menu>
		</EntityCard>
	);
}

type EditAppDetailsDialogProps = {
	bundle: BundleDefinitionSummaryModel | null;
	details?: AppDetails;
	onClose: () => void;
	onSuccess: () => void;
};

function EditAppDetailsDialog({ bundle, details, onClose, onSuccess }: EditAppDetailsDialogProps) {
	const client = useApiClient(UserInterfaceClient);
	const toastService = useToastService();
	const { data: loadedBundle } = useQuery({
		queryKey: ['userinterface', 'bundles', 'definition', 'edit', bundle?.definitionId, bundle?.changeSetId],
		queryFn: () => client.getByDefinitionId(bundle!.definitionId!, bundle!.changeSetId),
		enabled: Boolean(bundle?.definitionId),
	});
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [saving, setSaving] = useState(false);
	const [isMaximized, setIsMaximized] = useState(false);

	const definition = details?.definition ?? loadedBundle?.definition;
	const loadedDetails = useMemo(() => getAppDetails(definition), [definition]);

	useEffect(() => {
		if (!bundle) return;
		setName(bundle.name);
		setDescription(loadedDetails.description ?? '');
		setSaving(false);
		setIsMaximized(false);
	}, [bundle, loadedDetails.description]);

	const handleSave = async () => {
		if (!bundle || !name.trim() || !definition || !bundle.changeSetId) return;

		try {
			setSaving(true);
			const updatedDefinition = JSON.parse(definition) as Bundle;
			updatedDefinition.Name = name.trim();
			updatedDefinition.Description = description.trim() || undefined;
			await client.update(bundle.id, {
				changeSetId: bundle.changeSetId,
				name: name.trim(),
				definition: JSON.stringify(updatedDefinition),
				tags: bundle.tags,
			});
			toastService.success(`App "${name.trim()}" updated successfully.`);
			onSuccess();
			onClose();
		} catch (error) {
			console.error('Error updating app:', error);
			toastService.error(`Failed to update app: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={Boolean(bundle)} onClose={() => !saving && onClose()} fullWidth maxWidth="sm" fullScreen={isMaximized}>
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<Typography component="span" variant="h6" sx={{ flex: 1 }}>
					Edit app
				</Typography>
				<DialogHeaderActions isMaximized={isMaximized} onToggleMaximized={() => setIsMaximized((value) => !value)} onClose={onClose} closeDisabled={saving} />
			</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ pt: 1 }}>
					{!definition && <Typography color="text.secondary">Loading app details…</Typography>}
					<TextField autoFocus label="Name" value={name} required fullWidth onChange={(event) => setName(event.target.value)} disabled={saving} />
					<TextField
						label="Description"
						value={description}
						fullWidth
						multiline
						minRows={3}
						onChange={(event) => setDescription(event.target.value)}
						disabled={saving}
					/>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={saving}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleSave} disabled={saving || !name.trim() || !definition || !bundle?.changeSetId}>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default function AppListView() {
	useDesignerScreenContext({ ScreenArea: 'Apps' });
	const client = useApiClient(UserInterfaceClient);
	const changeSetClient = useApiClient(ChangeSetClient);
	const toastService = useToastService();
	const { currentChangeSetId } = useChangeSet();
	const { paginationModel, setPaginationModel } = usePersistedTablePagination('bundleList.pageSize');
	const [searchText, setSearchText] = useState('');
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [selectedBundle, setSelectedBundle] = useState<BundleDefinitionSummaryModel | null>(null);
	const [editingBundle, setEditingBundle] = useState<BundleDefinitionSummaryModel | null>(null);
	const [openCreateDialog, setOpenCreateDialog] = useState(false);
	const [versionsBundle, setVersionsBundle] = useState<BundleDefinitionSummaryModel | null>(null);
	const normalizedSearchText = searchText.trim();

	const { data, isLoading, refetch } = useQuery({
		queryKey: ['userinterface', 'bundles', 'list', paginationModel, currentChangeSetId, normalizedSearchText],
		queryFn: () => client.search(currentChangeSetId ?? undefined, undefined, normalizedSearchText || undefined, paginationModel.page, paginationModel.pageSize),
	});
	const { data: changeSetsData } = useQuery({
		queryKey: ['changeSets', 'all'],
		queryFn: () => changeSetClient.search(undefined, undefined, 0, 500, undefined, undefined),
	});

	const changeSetNameById = useMemo<Map<string, string>>(
		() => new Map(changeSetsData?.items.map((changeSet) => [changeSet.id, changeSet.name])),
		[changeSetsData],
	);
	const bundles = useMemo(() => data?.items ?? [], [data?.items]);
	const bundleDefinitionQueries = useQueries({
		queries: bundles.map((bundle) => ({
			queryKey: ['userinterface', 'bundles', 'definition', bundle.definitionId, currentChangeSetId ?? bundle.changeSetId],
			queryFn: () =>
				bundle.definitionId ? client.getByDefinitionId(bundle.definitionId, currentChangeSetId ?? bundle.changeSetId) : client.getById(bundle.id),
			staleTime: 5 * 60 * 1000,
		})),
	});
	const appDetailsById = useMemo(() => {
		const details = new Map<string, AppDetails>();
		bundles.forEach((bundle, index) => details.set(bundle.id, getAppDetails(bundleDefinitionQueries[index]?.data?.definition)));
		return details;
	}, [bundleDefinitionQueries, bundles]);
	const appTotals = useMemo(
		() =>
			bundles.reduce(
				(totals, bundle) => ({
					views: totals.views + (bundle.statistics?.viewCount ?? 0),
					components: totals.components + (bundle.statistics?.componentCount ?? 0),
					resources: totals.resources + (bundle.statistics?.resourceCount ?? 0),
					operations: totals.operations + (bundle.statistics?.operationCount ?? 0),
					contexts: totals.contexts + (bundle.statistics?.contextCount ?? 0),
				}),
				{ views: 0, components: 0, resources: 0, operations: 0, contexts: 0 },
			),
		[bundles],
	);

	const handleConfirmDelete = async () => {
		if (!selectedBundle) return;
		try {
			await client.delete(selectedBundle.id);
			toastService.success(`Bundle "${selectedBundle.name}" deleted successfully.`);
			refetch();
		} catch (error) {
			console.error('Error deleting bundle:', error);
			toastService.error(`Failed to delete bundle: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
		} finally {
			setOpenDeleteDialog(false);
			setSelectedBundle(null);
		}
	};

	const handleDownloadReactCode = async (bundle: BundleDefinitionSummaryModel) => {
		if (!bundle.definitionId) return;

		try {
			await downloadAppReactCode(client, bundle.definitionId, currentChangeSetId ?? bundle.changeSetId ?? undefined, `${bundle.name}-react.zip`);
			toastService.success('React code download started.');
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to download React code: ${errorMessage}`);
		}
	};

	return (
		<Stack spacing={2} sx={{ flexGrow: 1, minWidth: 0, minHeight: 0, p: (theme) => `${theme.layout.edgeGap}px`, overflow: 'auto' }}>
			<GalleryHeader
				title="Apps"
				subtitle="Build and manage customer experiences across your Randol environment."
				utilityActions={
					<>
						<Button component={Link} to="/resources" startIcon={<ArticleIcon />}>
							Resources
						</Button>
						<Tooltip title="Refresh apps">
							<IconButton aria-label="Refresh apps" onClick={() => refetch()}>
								<Refresh />
							</IconButton>
						</Tooltip>
					</>
				}
				primaryAction={
					<Button variant="contained" startIcon={<Add />} onClick={() => setOpenCreateDialog(true)}>
						Create app
					</Button>
				}
				statistics={
					<GalleryStatistics
						items={[
							{ label: 'Apps on this page', value: bundles.length },
							{ label: 'Views', value: appTotals.views },
							{ label: 'Components', value: appTotals.components },
							{ label: 'Media', value: appTotals.resources },
							{ label: 'Operations', value: appTotals.operations },
							{ label: 'Contexts', value: appTotals.contexts },
						]}
					/>
				}
				search={
					<TextField
						value={searchText}
						onChange={(event) => {
							setSearchText(event.target.value);
							setPaginationModel((current) => ({ ...current, page: 0 }));
						}}
						placeholder="Search apps…"
						size="small"
						fullWidth
						inputProps={{ 'aria-label': 'Search apps' }}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<Search fontSize="small" />
								</InputAdornment>
							),
						}}
					/>
				}
			/>

			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
					gap: 2,
					flexGrow: 1,
					alignItems: 'start',
					alignContent: 'start',
				}}
			>
				{bundles.map((bundle) => (
					<AppCard
						key={bundle.id}
						bundle={bundle}
						details={appDetailsById.get(bundle.id) ?? emptyAppDetails}
						onShowVersions={setVersionsBundle}
						onCopyId={(item) => {
							navigator.clipboard.writeText(item.id);
							toastService.success('ID copied to clipboard');
						}}
						onDownloadReactCode={handleDownloadReactCode}
						onEdit={setEditingBundle}
						onDelete={(item) => {
							setSelectedBundle(item);
							setOpenDeleteDialog(true);
						}}
					/>
				))}
				<EntityCardPlaceholder
					onClick={() => setOpenCreateDialog(true)}
					icon={
						<Box
							sx={{
								width: 54,
								height: 54,
								borderRadius: '50%',
								display: 'grid',
								placeItems: 'center',
								color: 'primary.main',
								backgroundColor: 'action.selected',
							}}
						>
							<Add fontSize="large" />
						</Box>
					}
					title={<Typography variant="h6">Create a new app</Typography>}
					description={
						<Typography variant="body2" color="text.secondary">
							Start from a template or create an app from scratch.
						</Typography>
					}
					action={
						<Button
							variant="contained"
							startIcon={<Add />}
							onClick={(event) => {
								event.stopPropagation();
								setOpenCreateDialog(true);
							}}
						>
							Create app
						</Button>
					}
					sx={{
						height: 320,
						p: 3,
					}}
				/>
			</Box>

			{isLoading && <Typography color="text.secondary">Loading apps…</Typography>}
			{!isLoading && bundles.length === 0 && normalizedSearchText && <Typography color="text.secondary">No apps match “{normalizedSearchText}”.</Typography>}
			<Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" sx={{ mt: 'auto', pt: 1 }}>
				<Button disabled={paginationModel.page === 0} onClick={() => setPaginationModel((current) => ({ ...current, page: current.page - 1 }))}>
					Previous
				</Button>
				<Typography variant="body2" color="text.secondary">
					Page {paginationModel.page + 1}
				</Typography>
				<Button disabled={!data?.hasNextPage} onClick={() => setPaginationModel((current) => ({ ...current, page: current.page + 1 }))}>
					Next
				</Button>
			</Stack>

			<ConfirmDialog
				open={openDeleteDialog}
				title="Delete Bundle"
				message={selectedBundle ? `Are you sure you want to delete "${selectedBundle.name}"? This action cannot be undone.` : ''}
				confirmText="Delete"
				confirmColor="error"
				onConfirm={handleConfirmDelete}
				onCancel={() => {
					setOpenDeleteDialog(false);
					setSelectedBundle(null);
				}}
			/>
			<CreateAppDialog open={openCreateDialog} setOpen={setOpenCreateDialog} onSuccess={() => refetch()} defaultChangeSetId={currentChangeSetId} />
			<EditAppDetailsDialog
				bundle={editingBundle}
				details={editingBundle ? appDetailsById.get(editingBundle.id) : undefined}
				onClose={() => setEditingBundle(null)}
				onSuccess={() => refetch()}
			/>
			<AppVersionsDialog bundle={versionsBundle} changeSetNameById={changeSetNameById} onClose={() => setVersionsBundle(null)} onDeleted={() => refetch()} />
		</Stack>
	);
}
